import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  TableSortLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import basePCUrl from '../../../Globals';
import { fetchApiData } from '../../../utils/service';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  rootEmbedded: {
    padding: 0,
    background: 'transparent',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  kpiGrid: {
    marginBottom: theme.spacing(4),
  },
  kpiCard: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.25)',
    },
    height: '100%',
  },
  tableContainer: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    background: 'linear-gradient(135deg, #061739 0%, #0055A2 50%, #428BC9 100%)',
    color: 'white',
    fontWeight: 600,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#0055A2',
    marginBottom: theme.spacing(2),
  },
}));

const getStatusStyle = (status) => {
  if (status === 'Completed') return { background: '#4CAF50', color: 'white' };
  if (status === 'In Progress') return { background: '#2196F3', color: 'white' };
  return { background: '#FF9800', color: 'white' };
};
const formatDuration = (secondsValue) => {
  if (secondsValue == null || secondsValue === '') return '—';

  const parsedSeconds = Number(secondsValue);
  if (!Number.isFinite(parsedSeconds) || parsedSeconds < 0) return '—';

  const totalSeconds = Math.round(parsedSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return seconds > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  return `${seconds}s`;
};


const isCompletedStatus = (status) => {
  return typeof status === 'string' && status.toLowerCase() === 'completed';
};

const isModuleCompleted = (module) => {
  if (!module) return false;
  if (isCompletedStatus(module.status)) return true;
  const completion = Number(module.completionPercentage);
  return Number.isFinite(completion) && completion >= 100;
};

const formatReportDate = (value) => {
  if (value == null || value === '') return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getModuleDueDate = (module) =>
  module?.dueDate ?? module?.due_date ?? module?.due ?? null;

const getModuleAssignedDate = (module) =>
  module?.assignedDate ??
  module?.assigned_date ??
  module?.dateAssigned ??
  module?.assignmentDate ??
  null;

const isPastDueModule = (module, now = new Date()) => {
  if (!module || isModuleCompleted(module)) return false;

  const normalizedStatus =
    typeof module.status === 'string' ? module.status.trim().toLowerCase() : '';
  if (normalizedStatus === 'overdue' || normalizedStatus === 'past due' || normalizedStatus.includes('overdue')) {
    return true;
  }

  const dueDateValue = getModuleDueDate(module);
  if (!dueDateValue) return false;
  const parsedDueDate = new Date(dueDateValue);
  if (Number.isNaN(parsedDueDate.getTime())) return false;

  const dueDateEnd = new Date(parsedDueDate);
  dueDateEnd.setHours(23, 59, 59, 999);
  return dueDateEnd < now;
};

const getRiskAssessment = (assignedTraining) => {
  const now = new Date();
  const requiredOutstandingCount = assignedTraining.filter((module) => !isModuleCompleted(module)).length;
  const overdueCount = assignedTraining.filter((module) => isPastDueModule(module, now)).length;

  if (requiredOutstandingCount >= 3 || overdueCount >= 3) {
    return {
      level: 'at_risk',
      label: 'At Risk',
      color: '#F44336',
      requiredOutstandingCount,
      overdueCount,
    };
  }

  if (requiredOutstandingCount >= 2 || overdueCount >= 1) {
    return {
      level: 'moderate',
      label: 'Moderate Risk',
      color: '#FFB300',
      requiredOutstandingCount,
      overdueCount,
    };
  }

  return {
    level: 'good',
    label: 'Looks Good',
    color: '#4CAF50',
    requiredOutstandingCount,
    overdueCount,
  };
};

const STATUS_PRIORITY = (status) => {
  if (status === 'Completed') return 2;
  if (status === 'In Progress') return 1;
  return 0;
};

const PASSFAIL_PRIORITY = (pf) => {
  if (pf === 'pass') return 2;
  if (pf === 'fail') return 1;
  return 0;
};

const getReportTitle = (ownerName, translate = (value) => value) => {
  const normalizedOwnerName = (ownerName || '').trim();
  if (!normalizedOwnerName) {
    return translate('My Training Report');
  }
  return `${normalizedOwnerName}'s ${translate('Training Report')}`;
};

const MyTrainingReport = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { getAccessTokenSilently } = usePortalAuth();
  const preferredLanguage = props.preferredLanguage;
  const getTranslation = props.getTranslation || ((value) => value);
  const t = (value) => getTranslation(value, preferredLanguage);
  const embedded = !!props.embedded;
  const [sortField, setSortField] = useState('assignedDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Newest-first is the natural default for date columns.
      setSortDirection(field === 'assignedDate' ? 'desc' : 'asc');
    }
  };

  const selectedClientId = props.selectedClient?.clientId ?? props.selectedClient;
  const reportTitle = getReportTitle(props.ownerName, t);

  // If a `currentUser` prop is provided by a parent (e.g. TeamTrainingReport when
  // a username is chosen from the searchable dropdown and the response only has
  // user data), use it directly and skip the live API fetch.
  const overrideCurrentUser = props.currentUser;

  const { isLoading, data: sourceData, isError, error } = useQuery(
    ['my-training-report-live', selectedClientId],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiData(basePCUrl, token, 'getTeamTrainingReport', props.accessLevel, selectedClientId);
    },
    {
      enabled: !!selectedClientId && !overrideCurrentUser,
      staleTime: 1000 * 60 * 15, // 15 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
    }
  );
  const currentUser = overrideCurrentUser ?? sourceData?.trainingReport?.currentUser;
  const assignedTraining = currentUser?.assignedTraining ?? [];
  const riskAssessment = getRiskAssessment(assignedTraining);

  const totalAssigned = assignedTraining.length;
  const completed = assignedTraining.filter(t => t.status === 'Completed').length;
  const inProgress = assignedTraining.filter(t => t.status === 'In Progress').length;
  const notStarted = assignedTraining.filter(t => t.status !== 'Completed' && t.status !== 'In Progress').length;

  const getSortableTimestamp = (value) => {
    if (value == null || value === '') return 0;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sortedTraining = [...assignedTraining].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'assignedDate') {
      return (
        (getSortableTimestamp(getModuleAssignedDate(a)) -
          getSortableTimestamp(getModuleAssignedDate(b))) *
        dir
      );
    }
    if (sortField === 'status') {
      return (STATUS_PRIORITY(a.status) - STATUS_PRIORITY(b.status)) * dir;
    }
    if (sortField === 'passFail') {
      return (PASSFAIL_PRIORITY(a.passFail) - PASSFAIL_PRIORITY(b.passFail)) * dir;
    }
    // Fallback: newest assigned first
    return (
      getSortableTimestamp(getModuleAssignedDate(b)) -
      getSortableTimestamp(getModuleAssignedDate(a))
    );
  });

  if (isLoading) {
    return <LoadingSpinner className="LinkTableSpinner" box="LinkTableSpinnerBox" />;
  }

  const rootClassName = embedded ? classes.rootEmbedded : classes.root;

  if (isError) {
    return (
      <div className={rootClassName}>
        <Alert severity="error">{t('Failed to load report')}: {error?.message || t('Unknown error')}</Alert>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className={rootClassName}>
        <Typography>{t('No report data available.')}</Typography>
      </div>
    );
  }


  return (
    <div className={rootClassName}>
      <Box className={classes.header} style={embedded ? { marginBottom: 16 } : undefined}>
        <Typography variant={embedded ? 'h5' : 'h3'} className={classes.title}>
          {reportTitle}
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} className={classes.kpiGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>{t('Overall Completion')}</Typography>
              <Typography variant="h4" style={{ color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>
                {(currentUser.overallCompletionRate ?? 0).toFixed(1)}%
              </Typography>
              <LinearProgress variant="determinate" value={currentUser.overallCompletionRate ?? 0} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>{t('Modules Assigned')}</Typography>
              <Typography variant="h4" style={{ color: '#2196F3', fontWeight: 700 }}>{totalAssigned}</Typography>
              <Typography variant="body2" color="textSecondary">{completed} {t('completed')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>{t('In Progress')}</Typography>
              <Typography variant="h4" style={{ color: '#FF9800', fontWeight: 700 }}>{inProgress}</Typography>
              <Typography variant="body2" color="textSecondary">{notStarted} {t('not started')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>{t('Risk Status')}</Typography>
              <Typography variant="h4" style={{ color: riskAssessment.color, fontWeight: 700 }}>
                {t(riskAssessment.label)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('Past Due')}: {riskAssessment.overdueCount} • {t('Required Outstanding')}: {riskAssessment.requiredOutstandingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* At-risk alert */}
      {riskAssessment.level === 'at_risk' && (
        <Alert severity="error" style={{ marginBottom: 24 }}>
          <strong>⚠️ {t('At Risk')}:</strong> {t('You have')} {riskAssessment.requiredOutstandingCount} {t('required module(s) outstanding')},
          {` ${t('including')} ${riskAssessment.overdueCount} ${t('past due')}. ${t('Priority')}: ${t('get this below 3 to return to good standing')}.`}
        </Alert>
      )}
      {riskAssessment.level === 'moderate' && (
        <Alert severity="warning" style={{ marginBottom: 24 }}>
          <strong>⚠️ {t('Moderate Risk')}:</strong> {t('You have')} {riskAssessment.requiredOutstandingCount} {t('required module(s)')}
          {` ${t('outstanding and')} ${riskAssessment.overdueCount} ${t('past due')}. ${t('Complete a few modules to move back to Looks Good')}.`}
        </Alert>
      )}

      {/* Training Modules Table */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>{t('Assigned Training Modules')}</Typography>
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell style={{ color: 'white' }}>{t('Module')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">
                  <TableSortLabel
                    active={sortField === 'assignedDate'}
                    direction={sortField === 'assignedDate' ? sortDirection : 'desc'}
                    onClick={() => handleSort('assignedDate')}
                    style={{ color: 'white' }}
                  >
                    {t('Assigned Date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ color: 'white' }} align="center">
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDirection : 'asc'}
                    onClick={() => handleSort('status')}
                    style={{ color: 'white' }}
                  >
                    {t('Status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Completion')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Estimated Duration')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">
                  <TableSortLabel
                    active={sortField === 'passFail'}
                    direction={sortField === 'passFail' ? sortDirection : 'asc'}
                    onClick={() => handleSort('passFail')}
                    style={{ color: 'white' }}
                  >
                    {t('Pass / Fail')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTraining.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" style={{ fontWeight: 600, padding: '24px 0' }}>
                      {t('No Training Data in The Last 12 Months')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedTraining.map((module) => {
                  const isActionable = module.status !== 'Completed' && module.status !== 'In Progress';
                  const dueDateValue = getModuleDueDate(module);
                  return (
                    <TableRow
                      hover
                      key={module.trainingId}
                      onClick={isActionable ? () => history.push(`/Dashboard/Training?pad=&id=${module.trainingId}`) : undefined}
                      style={isActionable ? { cursor: 'pointer' } : undefined}
                    >
                      <TableCell style={{ fontWeight: 600 }}>{module.moduleName}</TableCell>
                      <TableCell align="center">
                        {formatReportDate(getModuleAssignedDate(module))}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 4 }}>
                          <Chip
                            label={module.status}
                            size="small"
                            style={getStatusStyle(module.status)}
                          />
                          {!isCompletedStatus(module.status) && dueDateValue ? (
                            <Typography variant="caption" style={{ fontWeight: 600, color: '#B71C1C' }}>
                              {`${t('DUE')}: ${formatReportDate(dueDateValue)}`}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <LinearProgress
                            variant="determinate"
                            value={module.completionPercentage ?? 0}
                            style={{ width: 80, marginRight: 8 }}
                          />
                          <span style={{ minWidth: 36, fontWeight: 600 }}>
                            {module.completionPercentage ?? 0}%
                          </span>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{formatDuration(module.timeSpentMinutes)}</TableCell>
                      <TableCell align="center">
                        {module.passFail ? (
                          <Chip
                            label={module.passFail.toUpperCase()}
                            size="small"
                            style={{
                              background: module.passFail === 'pass' ? '#4CAF50' : '#F44336',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};

export default MyTrainingReport;
